import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, Package, Mail, ArrowRight, FileText, Truck, Calendar, CreditCard, Loader2, AlertCircle, Download, MapPin, User, Home } from 'lucide-react';
import { trackFunnelStep, FUNNEL_STEPS } from '../../utils/analytics';
import { formatPrice } from '../../utils/formatPrice';
import { useCart } from '../../context/CartContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [receiptData, setReceiptData] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const { clearCart } = useCart();
  const orderProcessed = useRef(false);
  
  // Check if this is a review order
  const params = new URLSearchParams(location.search);
  const isReviewOrder = params.get('review') === 'true';

  // Check for a cookie indicating we should clear the cart
  const checkClearCartCookie = () => {
    const cookies = document.cookie.split(';');
    const clearCartCookie = cookies.find(cookie => cookie.trim().startsWith('dekalio_clear_cart='));
    
    if (clearCartCookie) {
      console.log('Found clear cart cookie, clearing cart...');
      clearCart();
      
      // Remove the cookie
      document.cookie = 'dekalio_clear_cart=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
    }
  };

  useEffect(() => {
    // Only process the order once
    if (orderProcessed.current) return;
    
    trackFunnelStep(FUNNEL_STEPS.PURCHASE_COMPLETE);
    
    // Get session ID from URL query parameters
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    const isReviewOrder = params.get('review') === 'true';
    
    // Handle review orders differently (no session_id)
    if (isReviewOrder) {
      setIsLoading(false);
      orderProcessed.current = true;
      clearCart();
      return;
    }
    
    if (sessionId) {
      // Create a function to handle the entire process
      const processOrder = async () => {
        try {
          setIsLoading(true);
          setError(null);
          
          // Mark the order as being processed to prevent duplicate processing
          orderProcessed.current = true;
          
          // Fetch session data
          const data = await fetchSessionData(sessionId);
          setReceiptData(data);
          console.log('Receipt data:', data);
          
          // Clear the cart
          clearCart();
          
          // Also check for the cookie (webhook-based cart clearing)
          checkClearCartCookie();
          
          // Small delay before syncing to Supabase to prevent resource exhaustion
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Sync order to Supabase in the background, only once
          syncOrderToSupabase(sessionId).catch(error => {
            console.error('Background sync to Supabase failed:', error);
            // Don't set error state as this is a background operation
          });
          
        } catch (error) {
          console.error('Error processing order:', error);
          setError('Det gick inte att ladda din orderbekräftelse. Vänligen kontakta kundtjänst.');
          // Reset the flag if there was an error
          orderProcessed.current = false;
        } finally {
          setIsLoading(false);
        }
      };
      
      processOrder();
    } else {
      setIsLoading(false);
      setError('No session ID found. Your order may still be processing.');
    }
  }, [location.search, clearCart]); // Only depend on location.search, not the entire location object

  const syncOrderToSupabase = async (sessionId) => {
    const maxRetries = 3;
    let retryCount = 0;
    let lastError = null;

    while (retryCount < maxRetries) {
      try {
        console.log(`Syncing order with session ID ${sessionId} to Supabase...`);
        const response = await fetch('/.netlify/functions/sync-order-to-supabase', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ sessionId }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('Order synced successfully:', data);
        return data;
      } catch (error) {
        lastError = error;
        retryCount++;
        if (retryCount < maxRetries) {
          // Exponential backoff: 1s, 2s, 4s
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
        }
      }
    }
    throw lastError;
  };

  const fetchSessionData = async (sessionId) => {
    const maxRetries = 3;
    let retryCount = 0;
    let lastError = null;

    while (retryCount < maxRetries) {
      try {
        const response = await fetch(
          `/.netlify/functions/get-session?session_id=${sessionId}`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return data;
      } catch (error) {
        lastError = error;
        retryCount++;
        if (retryCount < maxRetries) {
          // Exponential backoff: 1s, 2s, 4s
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
        }
      }
    }
    throw lastError;
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('sv-SE', options);
  };

  const formatPaymentMethod = (paymentMethodTypes, paymentMethodType) => {
    // Try to use the actual payment method type from payment intent first
    if (paymentMethodType) {
      switch (paymentMethodType) {
        case 'card':
          return 'Betalkort';
        case 'klarna':
          return 'Klarna';
        default:
          return paymentMethodType.charAt(0).toUpperCase() + paymentMethodType.slice(1);
      }
    }
    
    // Fall back to payment method types from session
    if (paymentMethodTypes && paymentMethodTypes.length > 0) {
      const method = paymentMethodTypes[0];
      switch (method) {
        case 'card':
          return 'Betalkort';
        case 'klarna':
          return 'Klarna';
        default:
          return method.charAt(0).toUpperCase() + method.slice(1);
      }
    }
    
    return 'Betalkort'; // Default fallback
  };

  const loadImageAsDataURL = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = url;
    });
  };

  const downloadReceipt = async () => {
    if (!receiptData) return;
    
    setIsGeneratingPDF(true);
    try {
      // Create new PDF document
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      let yPos = 20;

      // Header
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text('DEKALIO', pageWidth / 2, yPos, { align: 'center' });
      
      yPos += 10;
      doc.setFontSize(18);
      doc.text('Orderbekräftelse', pageWidth / 2, yPos, { align: 'center' });
      
      yPos += 15;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Ordernummer: ${receiptData.session.id.slice(-8).toUpperCase()}`, pageWidth / 2, yPos, { align: 'center' });
      
      yPos += 5;
      doc.text(`Datum: ${formatDate(receiptData.session.created)}`, pageWidth / 2, yPos, { align: 'center' });
      
      yPos += 15;

      // Customer and Shipping Info in two columns
      const leftCol = 15;
      const rightCol = pageWidth / 2 + 5;

      // Customer Info
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Kunduppgifter', leftCol, yPos);
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      yPos += 7;
      if (receiptData.session.customer) {
        doc.text(receiptData.session.customer.name || '', leftCol, yPos);
        yPos += 5;
        doc.text(receiptData.session.customer.email || '', leftCol, yPos);
      }
      
      // Reset yPos for shipping info
      yPos = yPos - 12;

      // Shipping Info
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Leveransadress', rightCol, yPos);
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      yPos += 7;
      if (receiptData.session.shipping) {
        doc.text(receiptData.session.shipping.name || '', rightCol, yPos);
        yPos += 5;
        doc.text(receiptData.session.shipping.address.line1 || '', rightCol, yPos);
        yPos += 5;
        if (receiptData.session.shipping.address.line2) {
          doc.text(receiptData.session.shipping.address.line2, rightCol, yPos);
          yPos += 5;
        }
        doc.text(`${receiptData.session.shipping.address.postal_code || ''} ${receiptData.session.shipping.address.city || ''}`, rightCol, yPos);
        yPos += 5;
        doc.text(receiptData.session.shipping.address.country || '', rightCol, yPos);
      }

      yPos += 15;

      // Order Items Table
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Beställda produkter', leftCol, yPos);
      yPos += 5;

      const tableData = receiptData.session.line_items.map(item => [
        item.description || '',
        `${item.quantity} st`,
        `${formatPrice(item.amount_total / 100)} kr`
      ]);

      autoTable(doc, {
        startY: yPos,
        head: [['Produkt', 'Antal', 'Pris']],
        body: tableData,
        theme: 'striped',
        headStyles: {
          fillColor: [0, 0, 0],
          textColor: [255, 255, 255],
          fontStyle: 'bold'
        },
        styles: {
          fontSize: 10,
          cellPadding: 5
        },
        columnStyles: {
          0: { cellWidth: 100 },
          1: { cellWidth: 30, halign: 'center' },
          2: { cellWidth: 40, halign: 'right' }
        }
      });

      yPos = doc.lastAutoTable.finalY + 15;

      // Payment Summary
      const summaryStartX = pageWidth - 80;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Delsumma:', summaryStartX, yPos);
      doc.text(`${formatPrice(receiptData.session.amount_total / 100 * 0.8)} kr`, summaryStartX + 50, yPos, { align: 'right' });
      
      yPos += 6;
      doc.text('Moms (25%):', summaryStartX, yPos);
      doc.text(`${formatPrice(receiptData.session.amount_total / 100 * 0.2)} kr`, summaryStartX + 50, yPos, { align: 'right' });
      
      yPos += 8;
      doc.setDrawColor(0);
      doc.line(summaryStartX, yPos, summaryStartX + 50, yPos);
      
      yPos += 6;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Totalt:', summaryStartX, yPos);
      doc.text(`${formatPrice(receiptData.session.amount_total / 100)} kr`, summaryStartX + 50, yPos, { align: 'right' });
      
      yPos += 10;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const paymentMethod = formatPaymentMethod(receiptData.session.payment_method_types, receiptData.session.payment_method_type);
      doc.text(`Betalmetod: ${paymentMethod}`, summaryStartX, yPos);
      yPos += 5;
      doc.text(`Status: ${receiptData.session.payment_status === 'paid' ? 'Betald' : 'Väntande'}`, summaryStartX, yPos);

      yPos += 20;

      // Add design previews if available
      if (receiptData?.orderData?.cartItems && receiptData.orderData.cartItems.length > 0) {
        for (const item of receiptData.orderData.cartItems) {
          // Add a new page for designs
          doc.addPage();
          yPos = 20;

          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          doc.text(`Din design: ${item.car?.name || 'Design'}`, leftCol, yPos);
          yPos += 10;

          if (item.previewImages && Object.keys(item.previewImages).length > 0) {
            // Sort views with "Sida Vänster" first
            const viewOrder = ['Sida Vänster', 'Sida Höger', 'Fram', 'Bak', 'Topp'];
            const sortedViews = [];
            
            // Add views in preferred order
            for (const view of viewOrder) {
              if (item.previewImages[view]) {
                sortedViews.push(view);
              }
            }
            
            // Add any remaining views not in the order list
            for (const view of Object.keys(item.previewImages)) {
              if (!sortedViews.includes(view)) {
                sortedViews.push(view);
              }
            }

            // Add each view
            for (const view of sortedViews) {
              const imageUrl = item.previewImages[view];
              
              if (imageUrl) {
                try {
                  const imgData = await loadImageAsDataURL(imageUrl);
                  const imgWidth = 160;
                  const imgHeight = 80;
                  
                  // Check if we need a new page (need space for label + image)
                  if (yPos + imgHeight + 15 > doc.internal.pageSize.height - 20) {
                    doc.addPage();
                    yPos = 20;
                  }
                  
                  doc.setFontSize(11);
                  doc.setFont('helvetica', 'bold');
                  doc.text(view, leftCol, yPos);
                  yPos += 7;

                  // Center the image
                  const imgX = (pageWidth - imgWidth) / 2;
                  doc.addImage(imgData, 'PNG', imgX, yPos, imgWidth, imgHeight);
                  yPos += imgHeight + 12;
                } catch (error) {
                  console.error(`Error loading image for ${view}:`, error);
                  doc.setFontSize(10);
                  doc.setFont('helvetica', 'italic');
                  doc.text(`${view}: (Kunde inte laddas)`, leftCol, yPos);
                  yPos += 10;
                }
              }
            }
          } else {
            doc.setFontSize(10);
            doc.setFont('helvetica', 'italic');
            doc.text('(Ingen förhandsvisning tillgänglig för denna design)', leftCol, yPos);
            yPos += 10;
          }
        }
      }

      // Add a new page for the "What happens next" section
      doc.addPage();
      yPos = 20;

      // Footer (yPos already set by previous section)
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Vad händer nu?', leftCol, yPos);
      
      yPos += 7;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text('1. Du får en bekräftelse via e-post', leftCol + 3, yPos);
      yPos += 5;
      doc.text('2. Vi börjar producera dina dekaler med högsta kvalitet', leftCol + 3, yPos);
      yPos += 5;
      doc.text('3. Leverans till angiven adress inom 1-3 arbetsdagar', leftCol + 3, yPos);
      
      yPos += 15;
      doc.setFont('helvetica', 'bold');
      doc.text('Kontakt:', leftCol, yPos);
      yPos += 5;
      doc.setFont('helvetica', 'normal');
      doc.text('E-post: info@dekalio.nu', leftCol + 3, yPos);
      yPos += 5;
      doc.text('Om du har några frågor, tveka inte att kontakta oss!', leftCol + 3, yPos);

      // Save PDF
      doc.save(`Dekalio-Kvitto-${receiptData.session.id.slice(-8).toUpperCase()}.pdf`);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Kunde inte generera PDF:en. Försök igen senare.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-secondary/50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center space-y-4 text-center">
              <div className="relative">
                <Package className="h-16 w-16 text-muted-foreground" />
                <Loader2 className="h-6 w-6 absolute -top-1 -right-1 animate-spin" />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Laddar din orderbekräftelse</h3>
                <p className="text-sm text-muted-foreground">
                  Vi hämtar dina orderdetaljer...
                </p>
              </div>
              <div className="w-full bg-secondary rounded-full h-2 overflow-hidden">
                <div className="bg-foreground h-full animate-pulse w-2/3"></div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Review order success state (no receipt needed)
  if (isReviewOrder && !isLoading) {
    return (
      <div className="min-h-screen bg-secondary/50 py-8 px-4">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Success Header */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex-shrink-0">
                  <div className="rounded-full bg-green-100 dark:bg-green-900/20 p-4">
                    <CheckCircle className="h-12 w-12 text-green-600 dark:text-green-400" />
                  </div>
                </div>
                <div className="flex-1 text-center md:text-left space-y-2">
                  <h1 className="text-3xl font-bold">Din design har skickats för granskning!</h1>
                  <p className="text-muted-foreground text-lg">
                    Vi går igenom din design och återkommer inom 1-2 arbetsdagar med feedback och betalningslänk.
                  </p>
                  <Badge variant="secondary" className="text-base px-3 py-1">
                    <FileText className="h-4 w-4 mr-2" />
                    Status: Granskning
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Review Process Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-xl">
                <Package className="mr-2 h-6 w-6 text-primary" />
                Vad händer nu?
              </CardTitle>
              <CardDescription>
                Så här går granskningsprocessen till
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                    1
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Expert granskning</h3>
                    <p className="text-sm text-muted-foreground">
                      Vår designexpert går igenom hela din bildesign noggrant
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                    2
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Kontroll av placering och proportioner</h3>
                    <p className="text-sm text-muted-foreground">
                      Vi kontrollerar att alla dekaler sitter optimalt och ser bra ut från alla vinklar
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                    3
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Feedback på läsbarhet och synlighet</h3>
                    <p className="text-sm text-muted-foreground">
                      Du får professionell feedback på textläsbarhet, kontraster och övergripande synlighet
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                    4
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Betalningslänk skickas</h3>
                    <p className="text-sm text-muted-foreground">
                      Efter godkänd granskning skickar vi en säker betalningslänk via e-post
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <Alert>
            <Mail className="h-4 w-4" />
            <AlertTitle>Behöver du hjälp eller har frågor?</AlertTitle>
            <AlertDescription>
              Kontakta oss på <a href="mailto:info@dekalio.nu" className="font-medium underline">info@dekalio.nu</a> så hjälper vi dig gärna.
            </AlertDescription>
          </Alert>

          {/* Action Buttons */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="flex-1"
                >
                  <Home className="mr-2 h-4 w-4" />
                  Till startsidan
                </Button>
                <Button
                  onClick={() => navigate('/customize')}
                  className="flex-1"
                >
                  <span>Skapa fler dekaler</span>
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-secondary/50 flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full">
          <CardHeader className="text-center pb-3">
            <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-10 w-10 text-destructive" />
            </div>
            <CardTitle className="text-2xl">Det uppstod ett problem</CardTitle>
            <CardDescription className="text-base mt-2">{error}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <Mail className="h-4 w-4" />
              <AlertTitle>Behöver du hjälp?</AlertTitle>
              <AlertDescription>
                Kontakta oss på <a href="mailto:info@dekalio.nu" className="font-medium underline">info@dekalio.nu</a> så hjälper vi dig.
              </AlertDescription>
            </Alert>
            <div className="flex gap-3">
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="flex-1"
              >
                <Home className="mr-2 h-4 w-4" />
                Startsida
              </Button>
              <Button
                onClick={() => navigate('/customize')}
                className="flex-1"
              >
                <span>Designa dekaler</span>
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state with receipt
  return (
    <div className="min-h-screen bg-secondary/50 py-8 px-4">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Success Header */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-shrink-0">
                <div className="rounded-full bg-secondary p-4">
                  <CheckCircle className="h-12 w-12" />
                </div>
              </div>
              <div className="flex-1 text-center md:text-left space-y-2">
                <h1 className="text-3xl font-bold">Tack för din beställning!</h1>
                <p className="text-muted-foreground text-lg">
                  Din order är nu bekräftad och vi börjar bearbeta den direkt.
                </p>
                {receiptData?.session?.id && (
                  <div className="flex items-center justify-center md:justify-start gap-2 pt-2">
                    <Badge variant="secondary" className="text-base px-3 py-1">
                      <Package className="h-4 w-4 mr-2" />
                      Order: {receiptData.session.id.slice(-8).toUpperCase()}
                    </Badge>
                    <Badge variant="outline" className="text-base px-3 py-1">
                      <Calendar className="h-4 w-4 mr-2" />
                      {formatDate(receiptData.session.created)}
                    </Badge>
                  </div>
                )}
              </div>
              {!isReviewOrder && (
                <div className="flex-shrink-0">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={downloadReceipt}
                    disabled={isGeneratingPDF}
                  >
                    {isGeneratingPDF ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Genererar...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        Ladda ner kvitto
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Customer & Shipping Info */}
        {receiptData && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Customer Info */}
            {receiptData.session.customer && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-lg">
                    <User className="mr-2 h-5 w-5" />
                    Kunduppgifter
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1">
                  <p className="font-medium">{receiptData.session.customer.name}</p>
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    {receiptData.session.customer.email}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Shipping Details */}
            {receiptData.session.shipping && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-lg">
                    <Truck className="mr-2 h-5 w-5" />
                    Leveransadress
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1">
                  <p className="font-medium">{receiptData.session.shipping.name}</p>
                  <div className="text-sm text-muted-foreground space-y-0.5">
                    <p className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span>
                        {receiptData.session.shipping.address.line1}
                        {receiptData.session.shipping.address.line2 && <><br />{receiptData.session.shipping.address.line2}</>}
                        <br />
                        {receiptData.session.shipping.address.postal_code} {receiptData.session.shipping.address.city}
                        <br />
                        {receiptData.session.shipping.address.country}
                      </span>
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Order Items & Payment */}
        {receiptData && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <Package className="mr-2 h-5 w-5" />
                Ordersammanfattning
              </CardTitle>
              <CardDescription>
                Dina beställda produkter och betalningsdetaljer
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Order Items */}
              <div className="space-y-4">
                {receiptData.session.line_items.map((item, index) => (
                  <div key={index} className="flex items-start justify-between pb-4 border-b last:border-0">
                    <div className="space-y-1 flex-1">
                      <p className="font-medium">{item.description}</p>
                      <p className="text-sm text-muted-foreground">
                        Antal: {item.quantity} st
                      </p>
                    </div>
                    <div className="text-right ml-4">
                      <p className="font-semibold">{formatPrice(item.amount_total / 100)} kr</p>
                    </div>
                  </div>
                ))}
              </div>

              <Separator />

              {/* Payment Summary */}
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Delsumma</span>
                  <span className="font-medium">{formatPrice(receiptData.session.amount_total / 100 * 0.8)} kr</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Moms (25%)</span>
                  <span className="font-medium">{formatPrice(receiptData.session.amount_total / 100 * 0.2)} kr</span>
                </div>
                
                <Separator />
                
                <div className="flex justify-between items-center pt-2">
                  <span className="text-lg font-bold">Totalt</span>
                  <span className="text-2xl font-bold">{formatPrice(receiptData.session.amount_total / 100)} kr</span>
                </div>
                
                <div className="flex items-center justify-end gap-2 pt-2 flex-wrap">
                  <Badge variant={receiptData.session.payment_status === 'paid' ? 'default' : 'secondary'} className="gap-1">
                    <CreditCard className="h-3 w-3" />
                    {receiptData.session.payment_status === 'paid' ? 'Betald' : 'Väntar på betalning'}
                  </Badge>
                  <Badge variant="outline" className="gap-1">
                    {formatPaymentMethod(receiptData.session.payment_method_types, receiptData.session.payment_method_type)}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Design Previews */}
        {receiptData?.orderData?.cartItems && receiptData.orderData.cartItems.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <FileText className="mr-2 h-5 w-5" />
                Din design
              </CardTitle>
              <CardDescription>
                Så här kommer dina dekaler att se ut
              </CardDescription>
            </CardHeader>
            <CardContent>
              {receiptData.orderData.cartItems.map((item, itemIndex) => (
                <div key={itemIndex} className="space-y-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline">{item.car?.name || 'Design'}</Badge>
                    {item.helfoliering && (
                      <Badge variant="secondary">Helfoliering</Badge>
                    )}
                    {item.previewImages && (
                      <span className="text-sm text-muted-foreground">
                        {Object.keys(item.previewImages).length} vyer
                      </span>
                    )}
                  </div>
                  
                  {/* Show preview images with tabs */}
                  {item.previewImages && Object.keys(item.previewImages).length > 0 ? (
                    <Tabs defaultValue={(() => {
                      // Prioritize "Sida Vänster" as default view
                      const viewOrder = ['Sida Vänster', 'Sida Höger', 'Fram', 'Bak', 'Topp'];
                      for (const view of viewOrder) {
                        if (item.previewImages[view]) return view;
                      }
                      return Object.keys(item.previewImages)[0];
                    })()} className="w-full">
                      <TabsList className="w-full justify-start flex-wrap h-auto">
                        {(() => {
                          // Sort tabs with "Sida Vänster" first
                          const viewOrder = ['Sida Vänster', 'Sida Höger', 'Fram', 'Bak', 'Topp'];
                          const sortedViews = [];
                          for (const view of viewOrder) {
                            if (item.previewImages[view]) sortedViews.push(view);
                          }
                          for (const view of Object.keys(item.previewImages)) {
                            if (!sortedViews.includes(view)) sortedViews.push(view);
                          }
                          return sortedViews.map((view) => (
                            <TabsTrigger key={view} value={view} className="text-xs">
                              {view}
                            </TabsTrigger>
                          ));
                        })()}
                      </TabsList>
                      {(() => {
                        // Use same sorted order for content
                        const viewOrder = ['Sida Vänster', 'Sida Höger', 'Fram', 'Bak', 'Topp'];
                        const sortedViews = [];
                        for (const view of viewOrder) {
                          if (item.previewImages[view]) sortedViews.push(view);
                        }
                        for (const view of Object.keys(item.previewImages)) {
                          if (!sortedViews.includes(view)) sortedViews.push(view);
                        }
                        return sortedViews.map((view) => {
                          const imageUrl = item.previewImages[view];
                          return (
                            <TabsContent key={view} value={view} className="mt-4">
                              {imageUrl ? (
                                <div className="rounded-lg overflow-hidden bg-muted/50 p-4">
                                  <img 
                                    src={imageUrl} 
                                    alt={`${item.car?.name || 'Design'} - ${view}`}
                                    className="w-full h-auto object-contain max-h-[400px] mx-auto"
                                    onContextMenu={(e) => e.preventDefault()}
                                    draggable="false"
                                  />
                                </div>
                              ) : (
                                <Alert>
                                  <AlertCircle className="h-4 w-4" />
                                  <AlertDescription>
                                    Bilden för denna vy är inte tillgänglig
                                  </AlertDescription>
                                </Alert>
                              )}
                            </TabsContent>
                          );
                        });
                      })()}
                    </Tabs>
                  ) : (
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        Förhandsvisning av design är inte tillgänglig för denna order
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  {itemIndex < receiptData.orderData.cartItems.length - 1 && (
                    <Separator className="my-6" />
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Next Steps Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center text-lg">
              <Truck className="mr-2 h-5 w-5" />
              Vad händer nu?
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary">
                  <Mail className="h-5 w-5" />
                </div>
                <div className="space-y-1">
                  <p className="font-medium">Bekräftelse via e-post</p>
                  <p className="text-sm text-muted-foreground">
                    Du kommer snart få en orderbekräftelse på {receiptData?.session?.customer?.email}
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary">
                  <Package className="h-5 w-5" />
                </div>
                <div className="space-y-1">
                  <p className="font-medium">Produktion startar</p>
                  <p className="text-sm text-muted-foreground">
                    Vi börjar tillverka dina dekaler med högsta kvalitet
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary">
                  <Truck className="h-5 w-5" />
                </div>
                <div className="space-y-1">
                  <p className="font-medium">Leverans</p>
                  <p className="text-sm text-muted-foreground">
                    Dina dekaler levereras till din angivna adress inom 1-3 arbetsdagar
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            size="lg"
            className="min-w-[200px]"
          >
            <Home className="mr-2 h-4 w-4" />
            Tillbaka till startsidan
          </Button>
          <Button
            onClick={() => navigate('/customize')}
            size="lg"
            className="min-w-[200px]"
          >
            <span>Designa fler dekaler</span>
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>

        {/* Support Info */}
        <Alert>
          <Mail className="h-4 w-4" />
          <AlertTitle>Behöver du hjälp?</AlertTitle>
          <AlertDescription>
            Kontakta vår kundtjänst på{' '}
            <a href="mailto:info@dekalio.nu" className="font-medium underline">
              info@dekalio.nu
            </a>{' '}
            om du har några frågor om din beställning.
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
};

export const PaymentSuccessPreview = () => {
  return <PaymentSuccess />;
};

export default PaymentSuccess;